import { createContext, useContext } from "react"

/**
 * Whose eyes the package editor looks through.
 *
 * "package": the editor edits a package that any license or add-on may carry;
 * an item is locked when the catalog says so, and "also in Basic" names the
 * other packages carrying it. The default.
 *
 * "customer": the editor edits one customer's OWN LIST (lic.CustomerAccessLists)
 * on the customer panel. Two things change in how the same components read:
 * a class the customer has CLOSED ("only what's listed") makes every item of
 * that class locked FOR THIS CUSTOMER, so the checkboxes decide for all of
 * them, not just the catalog-locked ones; and "also in" names what already
 * grants the item to this customer beyond the own list — the license or add-on
 * on a live license. The closed set is a draft: the mode
 * control in a class header flips it, and Save writes it with the list in
 * one transaction. With a user picked, each row can also say whether the
 * entitlement rule actually reaches that user.
 *
 * Through the customer lens a row reads as the ANSWER, not as the draft: a
 * row the product or a license already includes shows a fixed tick and says
 * which; the checkbox is left for what the own list decides (see
 * rowStatusOf). The own list is carried by the customer's live licenses, so
 * with none live its entries reach nobody, and the lens says so.
 *
 * The same lens, READ-ONLY and about one person (the user panel's What
 * they get): `reaches` is that person's own answer from the rule, every
 * row and header leads with it instead of a checkbox, the counts and
 * sentences are theirs, and nothing writes: the list is read here and
 * changed on the customer panel.
 */
export type FeatureGroupLens =
  | { kind: "package" }
  | {
      kind: "customer"
      customerName: string
      /** The draft: feature types closed for the customer (only what's listed). */
      closedFeatureTypeCodes: ReadonlySet<number>
      setClosed(featureTypeCode: number, closed: boolean): void
      /** What grants the code beyond the own list ("Basic, Team Diplom-is"), or null. */
      sourcesOf(code: string): string | null
      /** Live licenses that carry packages (the own list included) to every user. */
      liveLicenseCount: number
      /** When checking as a user: does the rule grant that user the code? */
      reaches?: (code: string) => boolean
      userName?: string
      /**
       * About the one person named in `userName`, and nothing writes: the
       * rows lead with their answer, the controls are gone, and the
       * footer with them.
       */
      isReadOnly?: boolean
    }

/** Whether the lens is the read-only view of one person's access. */
export function isPersonLens(lens: FeatureGroupLens): lens is Extract<
  FeatureGroupLens,
  { kind: "customer" }
> & {
  isReadOnly: true
  reaches: (code: string) => boolean
  userName: string
} {
  return (
    lens.kind === "customer" &&
    lens.isReadOnly === true &&
    lens.reaches !== undefined &&
    lens.userName !== undefined
  )
}

export const PACKAGE_LENS: FeatureGroupLens = { kind: "package" }

const FeatureGroupLensContext = createContext<FeatureGroupLens>(PACKAGE_LENS)

export const FeatureGroupLensProvider = FeatureGroupLensContext.Provider

export function useFeatureGroupLens() {
  return useContext(FeatureGroupLensContext)
}
